import React, { Component } from 'react';
import { buildPath } from '../utils/route_utils.js';
import { updateSEO } from '../utils/seo_utils';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import Breadcrumb from '../components/Breadcrumb';
import '../css/AboutUsPage.css';

class AboutUsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: this.props.lang,
      visibleSections: {}
    };
    this.update_lang = this.update_lang.bind(this);
    this.sectionRefs = [];
    this.observerCallback = this.observerCallback.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    updateSEO('about_us', this.state.lang, '/about/about_us');
    this.observer = new IntersectionObserver(this.observerCallback, {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    });
    this.sectionRefs.forEach(ref => {
      if (ref) this.observer.observe(ref);
    });
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
  }

  observerCallback(entries) {
    const updates = {};
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        updates[entry.target.dataset.section] = true;
      }
    });
    if (Object.keys(updates).length > 0) {
      this.setState(prev => ({ visibleSections: { ...prev.visibleSections, ...updates } }));
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.lang) {
      this.setState({ lang: nextProps.lang });
    }
  }

  update_lang(newLang) { this.props.updateLang(newLang); }

  navigate(route) { window.location.href = buildPath(this.state.lang, route); }

  registerRef(el, id) {
    if (el && !this.sectionRefs.includes(el)) {
      el.dataset.section = id;
      this.sectionRefs.push(el);
      if (this.observer) this.observer.observe(el);
    }
  }

  render() {
    const lang = this.state.lang;
    const vis = this.state.visibleSections;

    const hero = {
      tag: { zh: "ABOUT NEXUNI", en: "ABOUT NEXUNI", ja: "ABOUT NEXUNI" },
      title: {
        zh: "公司理念",
        en: "About Us",
        ja: "Nexuni について"
      },
      subtitle: {
        zh: "讓機器人真正落地上工，解決最難的自動化難題。",
        en: "Bringing AI Robots into the Real World",
        ja: "AIロボットを現実世界に導入する"
      }
    };

    const mainContent = {
      zh: "Nexuni 耐思尼股份有限公司成立於台灣新竹科學園區，致力於打造<b>真正能落地的 AI 機器人</b>。我們以Agentic AI為核心平台整合感知、控制、定位與決策能力，打破市面上機器人難以導入實際場域的困境。在 AI 與機器人技術日益成熟的時代，許多企業仍面臨「買得到設備，卻無法完成任務」的挑戰。市面上多數機器人廠商僅提供單一元件（如手臂、機器人底盤或感測器），實際部署時常因缺乏對「最後一段動作」的客製化彈性（例如多開一道門、多搬一個物件），導致整體方案無法真正上線運作。",
      en: "Nexuni Co., Ltd., founded in Taiwan's Hsinchu Science Park, is committed to building <b>AI robots designed for true real-world</b> deployment. Driven by our core platform based on agentic AI, we seamlessly integrate perception, control, localization, and decision-making to solve a critical industry gap: making robots work reliably outside the lab. While robotics technology has matured, companies still struggle with equipment that is available but not operationally capable. Most manufacturers offer only isolated components—like arms, bases, or sensors—causing systems to fail during actual deployment due to a lack of adaptability for final, task-specific actions, such as opening an unexpected door or bypassing an obstacle.",
      ja: "Nexuni（耐思尼股份有限公司）は、台湾の新竹サイエンスパークで設立され、<b>実環境で真に稼働できるAIロボット</b>の実現に尽力しています。私たちは、エージェンティックAI（Agentic AI）を基盤とするコアプラットフォームを通じて、感知、制御、自己位置推定、意思決定をシームレスに統合し、「ロボットを研究室の外で安定して動作させる」という業界の決定的なギャップを解消します。AIやロボット技術が進化を遂げた今日においても、多くの企業は「設備はあるのに、実際のタスクを完遂できない」という課題を抱えています。市場の大半のメーカーが提供するのはロボットアームや移動台車、センサーなどの個別コンポーネントに過ぎず、実際の運用現場では「想定外のドアを開ける」「突然の障害物を回避する」といった、タスク完遂に向けた「最後のアクション」における柔軟性が不足し、システム全体の稼働に至らないケースが多発しています。"
    };

    const secondContent = {
      zh: "Nexuni 掌握 AI 機器人完整的核心技術，並擁有一套完善的機器人資料搜集方法，能夠深入學習並理解客戶實際場域的工作需求，確保機器人成功落地。我們的平台專為<b>高危險、缺工產業</b>設計，針對焊接、安保、清潔、停車場與餐飲等高勞動強度場域，提供可落地、可擴充、可快速客製的機器人自動化解決方案，協助業主提升效率並減少對人力的依賴。我們集結來自 MIT、CMU、Cornell、台大、清大、交大等國際頂尖人才與科技顧問團隊，融合 AI、機器人與自動化技術，秉持「簡單、可靠、安全」三大核心價值，致力於打造能真正解決問題、走入現場的 AI 機器人。",
      en: "At Nexuni, we own the complete technology stack for AI robotics and have developed a comprehensive data collection methodology that enables robots to learn from and adapt to real-world customer environments. Our platform is designed for <b>industries facing labor shortages and high-risk conditions</b>. We provide deployable, scalable, and easily customizable robotic automation solutions for sectors such as welding, security, cleaning, parking management, and food service. Our team includes top engineers and expert consultants from institutions such as MIT, CMU, Cornell, National Taiwan University, National Tsing Hua University, and National Yang Ming Chiao Tung University. By combining advanced AI and robotics with a focus on simplicity, reliability, and safety, we deliver robots that are truly ready for the field.",
      ja: "Nexuniは、AIロボットの完全な技術スタックを自社で保有しており、現場の実運用に必要なデータを集めて学習する独自の手法も確立しています。私たちのプラットフォームは、<b>人手不足や危険な作業環境</b>に直面している業界向けに設計されており、溶接、警備、清掃、駐車場管理、飲食サービスなどの分野において、導入しやすく、拡張性があり、現場のニーズに応じてカスタマイズできるロボット自動化ソリューションを提供しています。Nexuniのチームは、MIT、CMU、Cornell、国立台湾大学、国立清華大学、国立陽明交通大学などのトップ大学出身のエンジニアや専門コンサルタントで構成されています。私たちは、高度なAIとロボティクスに、シンプルさ・信頼性・安全性を組み合わせることで、現場で本当に使えるロボットを実現しています。"
    };

    const closingContent = {
      zh: "我們不只是販售設備，而是交付能真正上工的解決方案。",
      en: "We don't just provide equipment. We deliver complete solutions that work.",
      ja: "私たちは、単なる設備ではなく、「現場で機能する完全なソリューション」を提供します。"
    };

    const tagline = "<< Nexuni AI Robots, Ready to Work. >>";

    const values = [
      {
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
        label: { zh: "簡單", en: "Simplicity", ja: "シンプルさ" },
        color: "var(--main-color)"
      },
      {
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
        label: { zh: "可靠", en: "Reliability", ja: "信頼性" },
        color: "var(--sub2-color)"
      },
      {
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>,
        label: { zh: "安全", en: "Safety", ja: "安全性" },
        color: "var(--sub1-color)"
      }
    ];

    return (
      <div id="body" className="about_us_page">
        <AnnouncementBanner lang={lang} />
        <Navbar lang={lang} updateLangFunc={this.update_lang} />
        <Breadcrumb lang={lang} items={[{ label: { zh: "關於我們", en: "About Us", ja: "会社について" } }]} />

        {/* Hero */}
        <section className="au_hero">
          <div className="au_hero_grid" />
          <div className="au_hero_content">
            <span className="au_hero_tag">{hero.tag[lang]}</span>
            <h1 className="au_hero_title">{hero.title[lang]}</h1>
            <p className="au_hero_subtitle">{hero.subtitle[lang]}</p>
          </div>
        </section>

        {/* Main Content */}
        <section
          className={"au_body" + (vis["body"] ? " au_visible" : "")}
          ref={el => this.registerRef(el, "body")}
        >
          <div className="au_body_inner">
            <div className="au_accent_line" />
            <p className="au_body_text" dangerouslySetInnerHTML={{ __html: mainContent[lang] }} />
          </div>
        </section>

        {/* Second block */}
        <section
          className={"au_mission" + (vis["mission"] ? " au_visible" : "")}
          ref={el => this.registerRef(el, "mission")}
        >
          <div className="au_mission_inner">
            <p className="au_mission_text" dangerouslySetInnerHTML={{ __html: secondContent[lang] }} />

            {/* Core Values */}
            <div className="au_values">
              {values.map((v, i) => (
                <div key={i} className="au_value_item" style={{ '--value-color': v.color }}>
                  <span className="au_value_icon">{v.icon}</span>
                  <span className="au_value_label">{v.label[lang]}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing + Tagline */}
        <section
          className={"au_closing" + (vis["closing"] ? " au_visible" : "")}
          ref={el => this.registerRef(el, "closing")}
        >
          <div className="au_closing_inner">
            <p className="au_closing_text">{closingContent[lang]}</p>
            <p className="au_tagline">{tagline}</p>
          </div>
        </section>

        {/* Partners */}
        {/* CTA */}
        <section className="au_cta">
          <button className="au_cta_btn" onClick={() => this.navigate('/contact')}>
            {{ zh: "聯絡我們", en: "Contact Us", ja: "お問い合わせ" }[lang]} &rarr;
          </button>
        </section>

        <Footer lang={lang} navigate={(route) => this.navigate(route)} />
      </div>
    );
  }
}

export default AboutUsPage;
